/**
 * Optimistic writes: Linear's transaction queue, on an allowlist
 * (back-end-rs/docs/SYNC_ENGINE.md §7, "Writes").
 *
 * A write is shown the moment it is made — its fields are laid over the
 * synced row (`SyncStore.setOverlay`) — then sent to the REST API in the
 * background. It stays laid over until the engine echoes the row back
 * with those values, so nothing flicks back in between. If the server
 * refuses it, it is taken off and the row shows what the server holds.
 * If the network is down it waits, in IndexedDB, and goes when it can,
 * reload or not. A row that changes underneath a waiting write gets the
 * write laid over its new state: the server decides the final order.
 *
 * **Only what is safe to send twice is on the list.** A write that was
 * applied but whose answer was lost is sent again after a reload, so
 * every operation here sets fields to values and has no other effect —
 * no email, no notification, no ledger entry. Money is never on it: the
 * bank does not deduplicate transfer references
 * (knowledge/notes/gotcha-transfer-reference-not-unique.md), and a fine
 * is a ledger charge. Each operation names the fields it may carry, and a
 * body naming any other is refused before anything is shown or sent.
 */
import { isAxiosError } from 'axios';
import {
  notificationService,
  violationService,
  workOrderService,
} from '../clients/ApiClients';
import {
  NotificationResponse,
  ViolationReturn,
  ViolationUpdate,
  WorkOrderReturn,
  WorkOrderUpdate,
} from '../clients/rust-api-client';
import { apiErrorMessage } from '../utils/apiErrors';
import { recordFines } from './fines';
import { ModelName } from './models';
import * as persist from './persist';
import { Overlay, SyncStore, syncStore } from './store';

// --- the allowlist ----------------------------------------------------------

export type WriteOp = 'violation.update' | 'workOrder.update' | 'notification.readStatus';

/** A violation's own fields. Not `fine_amount`: that posts a charge. */
const VIOLATION_FIELDS = [
  'status',
  'severity',
  'violation_type',
  'description',
  'message',
  'due_date',
  'follow_up_date',
] as const;

/** A work order's own fields. Not the costs, and not `home_id`, which
 *  moves it and everything on it. */
const WORK_ORDER_FIELDS = [
  'status',
  'priority',
  'assigned_to',
  'vendor_id',
  'vendor_name',
  'title',
  'description',
  'work_order_type',
  'scheduled_date',
  'completion_date',
  'notes',
] as const;

interface OpSpec {
  model: ModelName;
  /** The request fields this operation may carry. */
  fields: readonly string[];
  /** Send it. The rows the server returned, or null if it returns none. */
  send: (rowIds: string[], body: Record<string, unknown>) => Promise<unknown[] | null>;
}

export const OPS: Record<WriteOp, OpSpec> = {
  'violation.update': {
    model: 'violation',
    fields: VIOLATION_FIELDS,
    send: async ([id], body) => {
      const { data } = await violationService.updateViolation({
        violationId: id,
        violationUpdate: body as ViolationUpdate,
      });
      recordFines([data]);
      return [data];
    },
  },
  'workOrder.update': {
    model: 'workOrder',
    fields: WORK_ORDER_FIELDS,
    send: async ([id], body) => {
      const { data } = await workOrderService.updateWorkOrder({
        workOrderId: id,
        workOrderUpdate: body as WorkOrderUpdate,
      });
      return [data];
    },
  },
  /** Read or unread, for one notification or many. */
  'notification.readStatus': {
    model: 'notification',
    fields: ['is_read'],
    send: async (ids, body) => {
      const isRead = body.is_read === true;
      if (ids.length === 1) {
        const [notificationId] = ids;
        const { data } = await (isRead
          ? notificationService.markAsRead({ notificationId })
          : notificationService.markAsUnread({ notificationId }));
        return [data];
      }
      await notificationService.bulkUpdateReadStatus({
        bulkReadStatusUpdate: { notification_ids: ids, is_read: isRead },
      });
      return null;
    },
  },
};

// --- the queue --------------------------------------------------------------

interface Write {
  id: string;
  op: WriteOp;
  rowIds: string[];
  /** The request. */
  body: Record<string, unknown>;
  /** What each row shows until the engine echoes it: the server's values
   *  once it has answered, the caller's prediction until then. */
  shows: Record<string, Record<string, unknown>>;
  state: 'queued' | 'sending' | 'sent';
  attempts: number;
  createdAt: number;
  /** When the server accepted it. */
  sentAt?: number;
}

export interface WriteFailure {
  op: WriteOp;
  rowIds: string[];
  message: string;
}

/** An echo that never comes — the engine down, the row changed by a
 *  write that echoed first — must not pin the overlay forever. */
const ECHO_TIMEOUT_MS = 120_000;
/** A write older than this is not sent after a reload: by then somebody
 *  else may well have changed the row on purpose. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_BACKOFF_MS = 30_000;
/** Gateway errors are the network, not a refusal; try a few more times. */
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const MAX_ATTEMPTS = 8;

const newId = (): string => `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

export class WriteQueue {
  private writes: Write[] = [];
  private userId: string | null = null;
  private waiters = new Map<
    string,
    { resolve: (rows: unknown[] | null) => void; reject: (e: Error) => void }
  >();
  private failureListeners = new Set<(f: WriteFailure) => void>();
  private pendingListeners = new Set<() => void>();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private echoTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = 1000;
  private unsubscribe: (() => void) | null = null;
  private unsent = 0;
  /** Saves run one after another, so an older list never lands last. */
  private saving: Promise<void> = Promise.resolve();
  /** Bumped by every stop, so a start overtaken by another drops out. */
  private generation = 0;

  constructor(
    private readonly store: SyncStore,
    private readonly ops: Record<WriteOp, OpSpec> = OPS
  ) {}

  /** For a signed-in user: restore what they left unsent, and send it. */
  async start(userId: string): Promise<void> {
    this.stop();
    const generation = this.generation;
    this.userId = userId;
    this.unsubscribe = this.store.onServerChange((models) => this.retire(models));
    window.addEventListener('online', this.pump);
    const saved = await persist.loadWrites<Write>(userId);
    if (this.generation !== generation) return;
    const now = Date.now();
    saved.forEach((w) => {
      if (now - w.createdAt > MAX_AGE_MS) {
        this.fail(w, 'A change made over a day ago was never saved, and was dropped.');
        return;
      }
      this.writes.push({ ...w, state: 'queued' });
    });
    this.changed();
    this.pump();
  }

  /** Sign-out: forget everything. IndexedDB goes with the copy. */
  stop(): void {
    this.generation += 1;
    this.unsubscribe?.();
    this.unsubscribe = null;
    window.removeEventListener('online', this.pump);
    [this.retryTimer, this.echoTimer].forEach((t) => t && clearTimeout(t));
    this.retryTimer = this.echoTimer = null;
    this.waiters.forEach((w) => w.reject(new Error('Signed out before the change was saved.')));
    this.waiters.clear();
    this.writes = [];
    this.userId = null;
    this.backoff = 1000;
    this.updateUnsent();
  }

  /**
   * Make a change. Shown at once when there is a synced copy; resolves
   * with the rows the server returned once it accepts, rejects with its
   * reason if it refuses (the change has been taken off by then).
   *
   * Without a copy — the engine unreachable, or not started — there is
   * nothing to lay it over, so it is sent as a plain request and the
   * caller shows the answer itself.
   */
  run<R = unknown>(
    op: WriteOp,
    rowIds: string[],
    body: Record<string, unknown>,
    shows: Record<string, unknown>
  ): Promise<R[] | null> {
    const spec = this.ops[op];
    const stray = Object.keys(body).filter((k) => !spec.fields.includes(k));
    if (stray.length > 0) {
      return Promise.reject(
        new Error(`${op} cannot carry ${stray.join(', ')}: send it as a plain request.`)
      );
    }
    if (!this.userId || !this.store.isReady()) {
      return spec.send(rowIds, body) as Promise<R[] | null>;
    }
    const write: Write = {
      id: newId(),
      op,
      rowIds,
      body,
      shows: Object.fromEntries(rowIds.map((id) => [id, shows])),
      state: 'queued',
      attempts: 0,
      createdAt: Date.now(),
    };
    const done = new Promise<R[] | null>((resolve, reject) => {
      this.waiters.set(write.id, { resolve: resolve as never, reject });
    });
    this.writes.push(write);
    this.changed();
    this.pump();
    return done;
  }

  /** Told of every write the server refused that nobody was waiting on —
   *  one restored after a reload. */
  onFailure = (listener: (f: WriteFailure) => void): (() => void) => {
    this.failureListeners.add(listener);
    return () => {
      this.failureListeners.delete(listener);
    };
  };

  /** How many writes the server has not yet accepted. */
  unsentCount = (): number => this.unsent;

  subscribePending = (listener: () => void): (() => void) => {
    this.pendingListeners.add(listener);
    return () => {
      this.pendingListeners.delete(listener);
    };
  };

  // --- sending --------------------------------------------------------------

  /** Send every write whose rows have no earlier write still unsent: one
   *  row's writes go in order, different rows' at once. */
  private pump = (): void => {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    const busy = new Set<string>();
    this.writes.forEach((w) => {
      const keys = w.rowIds.map((id) => `${w.op}:${id}`);
      const blocked = keys.some((k) => busy.has(k));
      if (w.state !== 'sent') keys.forEach((k) => busy.add(k));
      if (w.state === 'queued' && !blocked) this.send(w);
    });
  };

  private async send(write: Write): Promise<void> {
    write.state = 'sending';
    write.attempts += 1;
    try {
      const rows = await this.ops[write.op].send(write.rowIds, write.body);
      if (!this.writes.includes(write)) return;
      // From now on the row shows what the server says it holds.
      rows?.forEach((row) => {
        const id = String((row as { id: unknown }).id);
        const fields = write.shows[id];
        if (!fields) return;
        write.shows[id] = Object.fromEntries(
          Object.keys(fields).map((k) => [k, (row as Record<string, unknown>)[k]])
        );
      });
      write.state = 'sent';
      write.sentAt = Date.now();
      this.backoff = 1000;
      this.waiters.get(write.id)?.resolve(rows);
      this.waiters.delete(write.id);
      this.retire([this.ops[write.op].model]);
      this.changed();
      this.scheduleEchoCheck();
      this.pump();
    } catch (err) {
      if (!this.writes.includes(write)) return;
      const status = isAxiosError(err) ? err.response?.status : undefined;
      const transient =
        isAxiosError(err) &&
        (status === undefined || RETRYABLE_STATUS.has(status)) &&
        write.attempts < MAX_ATTEMPTS;
      if (transient) {
        // The network, not the server: wait, and keep showing it.
        write.state = 'queued';
        this.persistWrites();
        this.scheduleRetry();
        return;
      }
      this.rollback(write, apiErrorMessage(err, "Couldn't save the change."));
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
    this.retryTimer = setTimeout(this.pump, delay);
  }

  private rollback(write: Write, message: string): void {
    this.writes = this.writes.filter((w) => w !== write);
    this.changed();
    const waiter = this.waiters.get(write.id);
    if (waiter) {
      this.waiters.delete(write.id);
      waiter.reject(new Error(message));
    } else {
      this.fail(write, message);
    }
    this.pump();
  }

  private fail(write: Pick<Write, 'op' | 'rowIds'>, message: string): void {
    const failure = { op: write.op, rowIds: write.rowIds, message };
    this.failureListeners.forEach((l) => l(failure));
  }

  // --- retiring -------------------------------------------------------------

  /**
   * A sent write is done with once the engine's copy of every row it
   * touched says what the server answered — the echo arrived — or it has
   * waited too long for one.
   */
  private retire(models: ModelName[]): void {
    const now = Date.now();
    const before = this.writes.length;
    this.writes = this.writes.filter((w) => {
      if (w.state !== 'sent') return true;
      const model = this.ops[w.op].model;
      if (!models.includes(model) && now - (w.sentAt ?? now) < ECHO_TIMEOUT_MS) return true;
      if (now - (w.sentAt ?? now) >= ECHO_TIMEOUT_MS) return false;
      return !w.rowIds.every((id) => {
        const row = this.store.serverRow(model, id) as Record<string, unknown> | undefined;
        // Gone from the copy: nothing left to lay it over.
        if (!row) return true;
        return Object.entries(w.shows[id] ?? {}).every(([k, v]) => same(row[k], v));
      });
    });
    if (this.writes.length !== before) this.changed();
  }

  private scheduleEchoCheck(): void {
    if (this.echoTimer) clearTimeout(this.echoTimer);
    if (!this.writes.some((w) => w.state === 'sent')) return;
    this.echoTimer = setTimeout(() => {
      this.echoTimer = null;
      this.retire([]);
      this.scheduleEchoCheck();
    }, ECHO_TIMEOUT_MS);
  }

  // --- the overlay and persistence ------------------------------------------------

  private changed(): void {
    const byModel = new Map<ModelName, Map<string, Record<string, unknown>>>();
    (Object.keys(this.ops) as WriteOp[]).forEach((op) =>
      byModel.set(this.ops[op].model, byModel.get(this.ops[op].model) ?? new Map())
    );
    // In order, so a later write to a field wins over an earlier one.
    this.writes.forEach((w) => {
      const rows = byModel.get(this.ops[w.op].model)!;
      w.rowIds.forEach((id) => rows.set(id, { ...rows.get(id), ...w.shows[id] }));
    });
    byModel.forEach((overlay, model) => this.store.setOverlay(model, overlay as Overlay));
    this.persistWrites();
    this.updateUnsent();
  }

  private persistWrites(): void {
    if (!this.userId) return;
    // Only what the server has not accepted: a sent write is already in
    // the database, and its echo will be in the copy.
    const userId = this.userId;
    const unsent = this.writes.filter((w) => w.state !== 'sent');
    this.saving = this.saving.then(() => persist.saveWrites(userId, unsent));
  }

  private updateUnsent(): void {
    const unsent = this.writes.filter((w) => w.state !== 'sent').length;
    if (unsent === this.unsent) return;
    this.unsent = unsent;
    this.pendingListeners.forEach((l) => l());
  }
}

/** The one queue the app writes through. Started by `SyncProvider`. */
export const writes = new WriteQueue(syncStore);

// --- typed entry points ---------------------------------------------------------

type Only<T, K extends string> = Pick<T, Extract<keyof T, K>>;

type ViolationFields = typeof VIOLATION_FIELDS[number];
type WorkOrderFields = typeof WORK_ORDER_FIELDS[number];

/** Requests take dates as epoch milliseconds; rows carry ISO strings. */
const DATE_FIELDS = new Set(['due_date', 'follow_up_date', 'scheduled_date', 'completion_date']);

/**
 * What a row will read once a request is saved, until the server says
 * exactly: the request's values, dates turned into the row's spelling.
 * The server's own answer replaces this as soon as it arrives.
 */
export const predict = (body: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(body).map(([k, v]) => [
      k,
      DATE_FIELDS.has(k) && typeof v === 'number' ? new Date(v).toISOString() : v,
    ])
  );

/**
 * Change a violation's own fields. `shows` corrects the prediction where
 * the request spells a value differently from the row.
 */
export const updateViolation = async (
  id: string,
  body: Only<ViolationUpdate, ViolationFields>,
  shows?: Partial<ViolationReturn>
): Promise<ViolationReturn | undefined> =>
  (
    await writes.run<ViolationReturn>('violation.update', [id], body, {
      ...predict(body),
      ...shows,
    })
  )?.[0];

/**
 * Change a work order's own fields. A request's status is `in_progress`
 * where the row's is `InProgress`: pass the row's in `shows`, or a board
 * grouping by it loses the card until the server answers.
 */
export const updateWorkOrder = async (
  id: string,
  body: Only<WorkOrderUpdate, WorkOrderFields>,
  shows?: Partial<WorkOrderReturn>
): Promise<WorkOrderReturn | undefined> =>
  (
    await writes.run<WorkOrderReturn>('workOrder.update', [id], body, {
      ...predict(body),
      ...shows,
    })
  )?.[0];

/** Whether every field of a request may go through the queue — the
 *  ones that may not (a fine, a cost) are sent as a plain request. */
export const isOptimistic = (op: WriteOp, body: object): boolean =>
  Object.keys(body).every((k) => OPS[op].fields.includes(k));

export const setNotificationsRead = async (
  ids: string[],
  isRead: boolean
): Promise<NotificationResponse[] | null> =>
  writes.run<NotificationResponse>(
    'notification.readStatus',
    ids,
    { is_read: isRead },
    { is_read: isRead }
  );
